// Reusable, performant, and responsive DataTable Web Component
// conformed to Shopify Polaris Web Components system and patterns.
import { escapeHtml } from '../utils/sanitize';

export interface OpmTableColumn {
  key: string;
  header: string;
  sortable?: boolean;
  sortType?: 'string' | 'number' | 'date';
  alignment?: 'left' | 'center' | 'right';
  render?: (row: any, column: OpmTableColumn) => string | HTMLElement;
  sortFunction?: (a: any, b: any) => number;
}

export class OpmDataTable extends HTMLElement {
  private _items: any[] = [];
  private _columns: OpmTableColumn[] = [];
  private _searchQuery: string = '';
  private _sortField: string | null = null;
  private _sortOrder: 'asc' | 'desc' | 'none' = 'none';
  private _currentPage: number = 1;
  private _pageSize: number = 10;
  private _originalOrderMap = new Map<any, number>(); // Preserve insertion order for "none" sort
  private _selectedIds: Set<string> = new Set();
  private _visibleItems: any[] = [];

  // Custom filter logic hook
  public filterFunction?: (item: any, query: string) => boolean;

  static get observedAttributes() {
    return ['searchable', 'paginated', 'page-size', 'current-page', 'total-count', 'selectable'];
  }

  constructor() {
    super();
  }

  connectedCallback() {
    // Resolve pre-upgrade property shadowing — if layout scripts set
    // properties before the element was upgraded, own-properties shadow
    // the class setters. Read, delete, and re-set so setters fire properly.
    this._upgradeProperty('items');
    this._upgradeProperty('columns');
    this._upgradeProperty('filterFunction');
    this._upgradeProperty('totalCount');
    this._upgradeProperty('selectable');

    this.parseAttributes();
    this.renderLayoutOnce();
    this.updateTable();
    this.setupListeners();
  }

  /**
   * Reads an expando own-property set before element upgrade,
   * deletes it, then re-assigns through the class setter.
   */
  private _upgradeProperty(prop: string) {
    if (this.hasOwnProperty(prop)) {
      const value = (this as any)[prop];
      delete (this as any)[prop];
      (this as any)[prop] = value;
    }
  }

  attributeChangedCallback(name: string, oldValue: string, newValue: string) {
    if (oldValue === newValue) return;
    this.parseAttributes();
    
    // If layout controls changed, we might need a layout refresh or data refresh
    if (name === 'searchable' || name === 'paginated' || name === 'selectable') {
      this.renderLayoutOnce(true);
    }
    
    this.updateTable();
  }

  private parseAttributes() {
    this._pageSize = Number(this.getAttribute('page-size') || '10');
    this._currentPage = Number(this.getAttribute('current-page') || '1');
  }

  // Properties Getters and Setters
  get items(): any[] {
    return this._items;
  }

  set items(val: any[]) {
    this._items = Array.isArray(val) ? val : [];
    // Reset order map
    this._originalOrderMap.clear();
    this._items.forEach((item, index) => {
      this._originalOrderMap.set(item, index);
    });
    this.updateTable();
  }

  get columns(): OpmTableColumn[] {
    return this._columns;
  }

  set columns(val: OpmTableColumn[]) {
    this._columns = Array.isArray(val) ? val : [];
    this.renderLayoutOnce(true);
    this.updateTable();
  }

  get searchable(): boolean {
    return this.hasAttribute('searchable') && this.getAttribute('searchable') !== 'false';
  }

  set searchable(val: boolean) {
    if (val) {
      this.setAttribute('searchable', 'true');
    } else {
      this.removeAttribute('searchable');
    }
  }

  get selectable(): boolean {
    return this.hasAttribute('selectable') && this.getAttribute('selectable') !== 'false';
  }

  set selectable(val: boolean) {
    if (val) {
      this.setAttribute('selectable', 'true');
    } else {
      this.removeAttribute('selectable');
    }
  }

  get selectedIds(): string[] {
    return Array.from(this._selectedIds);
  }

  set selectedIds(val: string[]) {
    this._selectedIds = new Set(val);
    this.updateTable();
  }

  get selectedId(): string | null {
    return this.getAttribute('selected-id');
  }

  set selectedId(val: string | null) {
    if (val) {
      this.setAttribute('selected-id', val);
    } else {
      this.removeAttribute('selected-id');
    }
    this.highlightSelectedRow();
  }

  public highlightSelectedRow() {
    const selId = this.selectedId;
    const rows = this.querySelectorAll('s-table-row');
    rows.forEach(row => {
      const rowBtn = row.querySelector('[data-id]');
      const rowId = row.getAttribute('data-row-id') || (rowBtn ? rowBtn.getAttribute('data-id') : null);
      if (selId && rowId === selId) {
        row.classList.add('ticket-row-active');
        row.setAttribute('data-selected', 'true');
      } else {
        row.classList.remove('ticket-row-active');
        row.removeAttribute('data-selected');
      }
    });
  }

  public clearSelection() {
    this._selectedIds.clear();
    const allCheckbox = this.querySelector('#table-select-all') as HTMLInputElement | null;
    if (allCheckbox) allCheckbox.checked = false;
    this.querySelectorAll('.table-row-checkbox').forEach(cb => {
      (cb as HTMLInputElement).checked = false;
    });
    this.dispatchEvent(new CustomEvent('selection-change', {
      detail: { selectedIds: this.selectedIds },
      bubbles: true,
      composed: true
    }));
  }

  get paginated(): boolean {
    return this.hasAttribute('paginated') && this.getAttribute('paginated') !== 'false';
  }

  set paginated(val: boolean) {
    if (val) {
      this.setAttribute('paginated', 'true');
    } else {
      this.removeAttribute('paginated');
    }
  }

  get pageSize(): number {
    return this._pageSize;
  }

  set pageSize(val: number) {
    this._pageSize = val;
    this.setAttribute('page-size', String(val));
    this.updateTable();
  }

  get currentPage(): number {
    return this._currentPage;
  }

  set currentPage(val: number) {
    this._currentPage = val;
    this.setAttribute('current-page', String(val));
    this.updateTable();
  }

  get totalCount(): number {
    return Number(this.getAttribute('total-count') || '0');
  }

  set totalCount(val: number) {
    this.setAttribute('total-count', String(val));
    this.updateTable();
  }

  get searchQuery(): string {
    return this._searchQuery;
  }

  set searchQuery(val: string) {
    this._searchQuery = val;
    const searchField = this.querySelector('#table-search-input') as any;
    if (searchField && searchField.value !== val) {
      searchField.value = val;
    }
    this.updateTable();
  }

  // Nested property resolver (e.g. resolve 'customer.name')
  private getNestedValue(obj: any, path: string): any {
    if (!path) return '';
    return path.split('.').reduce((acc, part) => acc && acc[part], obj);
  }

  // Renders the outer structural DOM once to avoid tearing down inputs/state
  private renderLayoutOnce(force: boolean = false) {
    const isAlreadyRendered = this.querySelector('.opm-datatable-container');
    if (isAlreadyRendered && !force) return;

    const placeholder = this.getAttribute('search-placeholder') || 'Search...';

    const searchHtml = this.searchable
      ? `
        <div class="opm-table-search-wrapper" style="padding: 1rem; border-bottom: 1px solid var(--p-color-border-subdued, #e1e3e5);">
          <s-text-field
            id="table-search-input"
            placeholder="${placeholder}"
            value="${this._searchQuery}"
            autocomplete="off"
            name="table-search"
          ></s-text-field>
        </div>
      `
      : '';

    const paginationHtml = this.paginated
      ? `
        <div class="opm-table-pagination-wrapper" style="
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 1rem;
          border-top: 1px solid var(--p-color-border-subdued, #e1e3e5);
          flex-wrap: wrap;
          gap: 0.75rem;
        ">
          <!-- Page Size Dropdown -->
          <div style="display: flex; align-items: center; gap: 0.5rem;">
            <s-text color="subdued">Rows per page</s-text>
            <select id="table-page-size" style="
              padding: 0.25rem 0.5rem;
              border: 1px solid var(--p-color-border-subdued, #e1e3e5);
              border-radius: var(--p-border-radius-100, 4px);
              background: var(--p-color-bg-surface, #ffffff);
              color: var(--p-color-text, #202223);
              font-size: 0.875rem;
              cursor: pointer;
              height: 32px;
            ">
              <option value="5" ${this._pageSize === 5 ? 'selected' : ''}>5</option>
              <option value="10" ${this._pageSize === 10 ? 'selected' : ''}>10</option>
              <option value="20" ${this._pageSize === 20 ? 'selected' : ''}>20</option>
              <option value="50" ${this._pageSize === 50 ? 'selected' : ''}>50</option>
            </select>
          </div>

          <!-- Navigation controls -->
          <div style="display: flex; align-items: center; gap: 1rem;">
            <s-text color="subdued" id="table-pagination-summary">Showing 0-0 of 0</s-text>
            <div style="display: flex; gap: 0.35rem;">
              <s-button id="table-prev-btn" size="slim">Previous</s-button>
              <s-button id="table-next-btn" size="slim">Next</s-button>
            </div>
          </div>
        </div>
      `
      : '';

    // Render columns headers HTML
    const checkboxHeaderHtml = this.selectable
      ? `
        <s-table-header style="width: 48px; text-align: center; vertical-align: middle;">
          <input type="checkbox" id="table-select-all" style="cursor: pointer; transform: scale(1.2); margin: 0; padding: 0;" />
        </s-table-header>
      `
      : '';

    const headersHtml = this._columns
      .map(col => {
        const isSortable = col.sortable;
        const align = col.alignment || 'left';
        const styleStr = `text-align: ${align}; ${isSortable ? 'cursor: pointer; user-select: none;' : ''}`;
        return `
          <s-table-header
            data-key="${col.key}"
            ${isSortable ? 'class="sortable-header"' : ''}
            style="${styleStr}"
          >
            <div style="display: inline-flex; align-items: center; gap: 0.25rem;">
              <span>${col.header}</span>
              <span class="sort-indicator" style="font-size: 0.75rem; color: var(--p-color-text-subdued, #6d7175);"></span>
            </div>
          </s-table-header>
        `;
      })
      .join('');

    this.innerHTML = `
      <div class="opm-datatable-container" style="display: flex; flex-direction: column; width: 100%;">
        ${searchHtml}
        <div class="opm-table-scroll-container" style="overflow-x: auto; width: 100%;">
          <s-table>
            <s-table-header-row id="table-header-row">
              ${checkboxHeaderHtml}
              ${headersHtml}
            </s-table-header-row>
            <s-table-body id="table-body">
              <!-- Rendered Dynamically -->
            </s-table-body>
          </s-table>
        </div>
        ${paginationHtml}
      </div>
    `;

    // Re-bind listeners for headers since DOM was rebuilt
    this.setupHeaderListeners();
  }

  // Binds interaction listeners for sorting and pagination controls
  private setupListeners() {
    // 1. Search Query Input
    this.addEventListener('input', (e: Event) => {
      const target = e.target as HTMLInputElement;
      if (target && target.id === 'table-search-input') {
        this._searchQuery = target.value || '';
        this._currentPage = 1; // reset page on filter change
        this.updateTable();
        this.dispatchEvent(new CustomEvent('search-change', {
          detail: { query: this._searchQuery },
          bubbles: true,
          composed: true
        }));
      }
    });

    // 2. Rows per Page selector
    this.addEventListener('change', (e: Event) => {
      const target = e.target as HTMLSelectElement;
      if (target && target.id === 'table-page-size') {
        this._pageSize = Number(target.value);
        this._currentPage = 1;
        this.setAttribute('page-size', String(this._pageSize));
        this.setAttribute('current-page', '1');
        this.updateTable();
        this.dispatchEvent(new CustomEvent('page-change', {
          detail: { page: this._currentPage, pageSize: this._pageSize },
          bubbles: true,
          composed: true
        }));
      }
    });

    // 3. Pagination click triggers
    this.addEventListener('click', (e: Event) => {
      const target = e.target as HTMLElement;
      
      const prevBtn = target.closest('#table-prev-btn');
      if (prevBtn && !prevBtn.hasAttribute('disabled')) {
        if (this._currentPage > 1) {
          this._currentPage--;
          this.setAttribute('current-page', String(this._currentPage));
          this.updateTable();
          this.dispatchEvent(new CustomEvent('page-change', {
            detail: { page: this._currentPage, pageSize: this._pageSize },
            bubbles: true,
            composed: true
          }));
        }
      }

      const nextBtn = target.closest('#table-next-btn');
      if (nextBtn && !nextBtn.hasAttribute('disabled')) {
        this._currentPage++;
        this.setAttribute('current-page', String(this._currentPage));
        this.updateTable();
        this.dispatchEvent(new CustomEvent('page-change', {
          detail: { page: this._currentPage, pageSize: this._pageSize },
          bubbles: true,
          composed: true
        }));
      }
    });

    // 4. Delegated Row Action listener
    this.addEventListener('click', (e: Event) => {
      const target = e.target as HTMLElement;
      
      // Check if clicked action button specifically first
      const actionEl = target.closest('[data-action]') as HTMLElement;
      if (actionEl) {
        const action = actionEl.getAttribute('data-action');
        const id = actionEl.getAttribute('data-id');
        if (action && id) {
          const item = this._items.find(x => String(x.id) === id);
          this.dispatchEvent(new CustomEvent('row-action', {
            detail: { action, id, item },
            bubbles: true,
            composed: true
          }));
          return;
        }
      }

      // Skip row selection if clicking checkbox, inputs, links, or other buttons
      if (
        target.closest('.table-row-checkbox') ||
        target.closest('#table-select-all') ||
        target.tagName === 'INPUT' ||
        target.closest('button') ||
        target.closest('a')
      ) {
        return;
      }

      // Fallback: Check if clicked a table row to trigger row click selection
      const rowEl = target.closest('s-table-row') as HTMLElement;
      if (rowEl) {
        const id = rowEl.getAttribute('data-row-id');
        if (id) {
          const item = this._items.find(x => String(x.id) === id);
          this.dispatchEvent(new CustomEvent('row-action', {
            detail: { action: 'view', id, item },
            bubbles: true,
            composed: true
          }));
        }
      }
    });

    // 5. Checkbox click / change triggers
    this.addEventListener('change', (e: Event) => {
      const target = e.target as HTMLInputElement;
      if (target && target.id === 'table-select-all') {
        const checked = target.checked;
        const visibleItems = this.getVisibleItems();
        visibleItems.forEach(item => {
          if (checked) {
            this._selectedIds.add(String(item.id));
          } else {
            this._selectedIds.delete(String(item.id));
          }
        });
        
        // Sync checkboxes in DOM
        this.querySelectorAll('.table-row-checkbox').forEach(cb => {
          (cb as HTMLInputElement).checked = checked;
        });

        this.dispatchEvent(new CustomEvent('selection-change', {
          detail: { selectedIds: this.selectedIds },
          bubbles: true,
          composed: true
        }));
      }

      if (target && target.classList.contains('table-row-checkbox')) {
        const id = target.getAttribute('data-row-id');
        if (id) {
          if (target.checked) {
            this._selectedIds.add(String(id));
          } else {
            this._selectedIds.delete(String(id));
          }

          // Update Select All checkbox state
          const allCheckbox = this.querySelector('#table-select-all') as HTMLInputElement | null;
          if (allCheckbox) {
            const visibleItems = this.getVisibleItems();
            const allChecked = visibleItems.length > 0 && visibleItems.every(item => this._selectedIds.has(String(item.id)));
            allCheckbox.checked = allChecked;
          }

          this.dispatchEvent(new CustomEvent('selection-change', {
            detail: { selectedIds: this.selectedIds },
            bubbles: true,
            composed: true
          }));
        }
      }
    });
  }

  // Bind click handlers to header columns for sorting
  private setupHeaderListeners() {
    const headerRow = this.querySelector('#table-header-row');
    if (!headerRow) return;

    headerRow.querySelectorAll('.sortable-header').forEach(header => {
      header.addEventListener('click', () => {
        const key = header.getAttribute('data-key');
        if (!key) return;

        if (this._sortField === key) {
          // Toggle order: asc -> desc -> none
          if (this._sortOrder === 'asc') {
            this._sortOrder = 'desc';
          } else if (this._sortOrder === 'desc') {
            this._sortOrder = 'none';
            this._sortField = null;
          } else {
            this._sortOrder = 'asc';
          }
        } else {
          this._sortField = key;
          this._sortOrder = 'asc';
        }

        this.updateTable();
      });
    });
  }

  // Calculates current view slice, sorts & filters, and re-renders table elements
  private updateTable() {
    const tableBody = this.querySelector('#table-body');
    if (!tableBody) return;

    // 1. Filtering
    let processed = [...this._items];
    if (this._searchQuery.trim() && !this.hasAttribute('total-count')) {
      const q = this._searchQuery.toLowerCase().trim();
      if (this.filterFunction) {
        processed = processed.filter(item => this.filterFunction!(item, q));
      } else {
        processed = processed.filter(item => {
          return this._columns.some(col => {
            if (!col.key) return false;
            const val = this.getNestedValue(item, col.key);
            if (val === undefined || val === null) return false;
            return String(val).toLowerCase().includes(q);
          });
        });
      }
    }

    // 2. Sorting
    if (this._sortField && this._sortOrder !== 'none') {
      const col = this._columns.find(c => c.key === this._sortField);
      const sortType = col?.sortType || 'string';

      processed.sort((a, b) => {
        if (col?.sortFunction) {
          const res = col.sortFunction(a, b);
          return this._sortOrder === 'asc' ? res : -res;
        }

        const valA = this.getNestedValue(a, this._sortField!);
        const valB = this.getNestedValue(b, this._sortField!);

        // Handle null/undefined values cleanly by sorting them to the bottom
        if (valA === undefined || valA === null) return 1;
        if (valB === undefined || valB === null) return -1;

        if (sortType === 'number') {
          const numA = Number(valA);
          const numB = Number(valB);
          return this._sortOrder === 'asc' ? numA - numB : numB - numA;
        }

        if (sortType === 'date') {
          const dateA = new Date(valA).getTime();
          const dateB = new Date(valB).getTime();
          return this._sortOrder === 'asc' ? dateA - dateB : dateB - dateA;
        }

        // Default: string comparison
        const strA = String(valA).toLowerCase();
        const strB = String(valB).toLowerCase();
        if (strA < strB) return this._sortOrder === 'asc' ? -1 : 1;
        if (strA > strB) return this._sortOrder === 'asc' ? 1 : -1;
        return 0;
      });
    } else {
      // Restore original insertion order if sort direction is none
      processed.sort((a, b) => {
        const idxA = this._originalOrderMap.get(a) ?? 0;
        const idxB = this._originalOrderMap.get(b) ?? 0;
        return idxA - idxB;
      });
    }

    // Update sort arrows/indicators in header
    this.updateHeaderSortIndicators();

    let totalCount = processed.length;
    if (this.hasAttribute('total-count')) {
      totalCount = Number(this.getAttribute('total-count')) || 0;
    }

    // 3. Pagination boundaries
    let pagedItems = processed;
    if (this.paginated) {
      const totalPages = Math.ceil(totalCount / this._pageSize) || 1;
      if (this._currentPage > totalPages) {
        this._currentPage = totalPages;
      }
      if (this._currentPage < 1) {
        this._currentPage = 1;
      }

      const startIdx = (this._currentPage - 1) * this._pageSize;
      const endIdx = startIdx + this._pageSize;

      if (this.hasAttribute('total-count')) {
        pagedItems = processed;
      } else {
        pagedItems = processed.slice(startIdx, endIdx);
      }

      // Update Pagination Bar UI elements
      const summaryEl = this.querySelector('#table-pagination-summary');
      if (summaryEl) {
        const from = totalCount === 0 ? 0 : startIdx + 1;
        const to = Math.min(endIdx, totalCount);
        summaryEl.textContent = `Showing ${from}–${to} of ${totalCount} results`;
      }

      const prevBtn = this.querySelector('#table-prev-btn') as any;
      if (prevBtn) {
        if (this._currentPage <= 1) {
          prevBtn.setAttribute('disabled', 'true');
        } else {
          prevBtn.removeAttribute('disabled');
        }
      }

      const nextBtn = this.querySelector('#table-next-btn') as any;
      if (nextBtn) {
        if (this._currentPage >= totalPages) {
          nextBtn.setAttribute('disabled', 'true');
        } else {
          nextBtn.removeAttribute('disabled');
        }
      }

      const pageSizeSelect = this.querySelector('#table-page-size') as HTMLSelectElement;
      if (pageSizeSelect) {
        pageSizeSelect.value = String(this._pageSize);
      }
    }

    // 4. Render Table Rows
    this._visibleItems = pagedItems;
    tableBody.innerHTML = '';
    
    if (pagedItems.length === 0) {
      const colCount = (this._columns.length || 1) + (this.selectable ? 1 : 0);
      const emptyMsg = escapeHtml(this.getAttribute('empty-message') || 'No data found.');
      tableBody.innerHTML = `
        <s-table-row>
          <s-table-cell colspan="${colCount}">
            <div style="padding: 2rem; text-align: center; color: var(--p-color-text-subdued, #6d7175);">
              ${emptyMsg}
            </div>
          </s-table-cell>
        </s-table-row>
      `;
      return;
    }

    pagedItems.forEach(row => {
      const tr = document.createElement('s-table-row');
      tr.setAttribute('data-row-id', String(row.id));
      tr.style.cursor = 'pointer';
      
      const selId = this.selectedId;
      if (selId && String(row.id) === selId) {
        tr.classList.add('ticket-row-active');
        tr.setAttribute('data-selected', 'true');
      }
      
      if (this.selectable) {
        const td = document.createElement('s-table-cell');
        td.style.textAlign = 'center';
        td.style.width = '48px';
        td.style.verticalAlign = 'middle';
        
        const isChecked = this._selectedIds.has(String(row.id));
        td.innerHTML = `
          <input type="checkbox" class="table-row-checkbox" data-row-id="${row.id}" ${isChecked ? 'checked' : ''} style="cursor: pointer; transform: scale(1.2); margin: 0; padding: 0;" />
        `;
        tr.appendChild(td);
      }

      this._columns.forEach(col => {
        const td = document.createElement('s-table-cell');
        const align = col.alignment || 'left';
        td.style.textAlign = align;

        if (col.render) {
          const rendered = col.render(row, col);
          if (rendered instanceof HTMLElement) {
            td.appendChild(rendered);
          } else {
            td.innerHTML = rendered;
          }
        } else {
          const val = this.getNestedValue(row, col.key);
          td.textContent = val !== undefined && val !== null ? String(val) : '';
        }

        tr.appendChild(td);
      });

      tableBody.appendChild(tr);
    });

    // Update Select All checkbox state based on visible items
    const allCheckbox = this.querySelector('#table-select-all') as HTMLInputElement | null;
    if (allCheckbox) {
      const visibleItems = this.getVisibleItems();
      const allChecked = visibleItems.length > 0 && visibleItems.every(item => this._selectedIds.has(String(item.id)));
      allCheckbox.checked = allChecked;
    }
  }

  private getVisibleItems(): any[] {
    return this._visibleItems || [];
  }

  // Refreshes unicode sort symbols depending on current sort state
  private updateHeaderSortIndicators() {
    const headers = this.querySelectorAll('.sortable-header');
    headers.forEach(header => {
      const key = header.getAttribute('data-key');
      const indicator = header.querySelector('.sort-indicator');
      if (!indicator) return;

      if (key === this._sortField) {
        if (this._sortOrder === 'asc') {
          indicator.textContent = ' ▲';
        } else if (this._sortOrder === 'desc') {
          indicator.textContent = ' ▼';
        } else {
          indicator.textContent = '';
        }
      } else {
        indicator.textContent = '';
      }
    });
  }
}

// Register web component
if (!customElements.get('opm-data-table')) {
  customElements.define('opm-data-table', OpmDataTable);
}
